import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

import styles from "./DeleteAccount.module.scss";
import penspaceLogo from "../../images/penspace-logo.png";
import Header from "../../components/Header";
import Footer from "../../components/Footer";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CODE_REGEX = /^\d{4,8}$/;

/**
 * Two steps: email a code, then delete with it. The page used to delete
 * whatever account the typed email belonged to (its password field was never
 * checked); now only someone who can read that mailbox can (release review
 * REL-03).
 */
export default function DeleteAccount() {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [emailError, setEmailError] = useState("");
  const [codeError, setCodeError] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [notice, setNotice] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    document.title = "Delete Account - Penspace";
    return () => {
      document.title = "Penspace";
    };
  }, []);

  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => setShowSuccess(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [showSuccess]);

  const validateEmail = () => {
    const trimmed = email.trim();
    if (!trimmed) {
      setEmailError("Email is required");
      return false;
    }
    if (!EMAIL_REGEX.test(trimmed)) {
      setEmailError("Please enter a valid email address");
      return false;
    }
    setEmailError("");
    return true;
  };

  const validateCode = () => {
    if (!CODE_REGEX.test(code.trim())) {
      setCodeError("Enter the code from the email we sent you");
      return false;
    }
    setCodeError("");
    return true;
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (emailError) validateEmail();
    // A different email needs its own code.
    if (codeSent) {
      setCodeSent(false);
      setCode("");
      setNotice("");
    }
  };

  const handleCodeChange = (e) => {
    setCode(e.target.value.replace(/\D/g, "").slice(0, 8));
    if (codeError) setCodeError("");
  };

  const call = async (path, payload) => {
    const apiRoot = process.env.REACT_APP_API_ROOT || "";
    const response = await fetch(`${apiRoot}/api/auth/${path}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    let data = {};
    try {
      data = await response.json();
    } catch (_) { }
    const ok = response.ok && (data.status === true || data.success === true);
    return { ok, message: (data && (data.message || data.error)) || "" };
  };

  const sendCode = async () => {
    if (!validateEmail()) return;
    setIsLoading(true);
    try {
      const { ok, message } = await call("delete-account/send-code", { email: email.trim() });
      if (ok) {
        setCodeSent(true);
        setNotice(message || "If an account uses this email, we've sent it a code.");
      } else {
        alert(message || "Could not send the code. Please try again.");
      }
    } catch (err) {
      console.error("Send code error:", err);
      alert("Something went wrong. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!codeSent) return sendCode();
    if (!validateEmail() || !validateCode()) return;

    setIsLoading(true);
    try {
      const { ok, message } = await call("delete-account", { email: email.trim(), otp: code.trim() });
      if (ok) {
        setShowSuccess(true);
        setEmail("");
        setCode("");
        setCodeSent(false);
        setNotice("");
        setEmailError("");
        setCodeError("");
      } else {
        alert(message || "Account deletion failed");
      }
    } catch (err) {
      console.error("Delete error:", err);
      alert("Something went wrong. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.wrapper}>
      <Header showHome />

      <section className={styles.deleteAccountPage}>
        <div className={styles.container}>
          <div className={styles.deleteAccountContainer}>
            <div className={styles.deleteAccountLogo}>
              <img src={penspaceLogo} alt="Penspace" />
            </div>

            <h1 className={styles.deleteAccountTitle}>Delete Account</h1>
            <p className={styles.deleteAccountSubtitle}>
              Deleting your account will permanently erase your personal data from Penspace.
              This includes:
            </p>
            <ul className={styles.dataList}>
              <li>Profile information (username, email)</li>
              <li>Reading progress, highlights, notes</li>
              <li>Gamification data (streaks, XP points, achievements)</li>
              <li>Preferences and saved recommendations</li>
            </ul>
            <p className={styles.infoText}>
              Some information (such as payment or transaction records) may be retained as required by law
              for tax, audit, or compliance purposes, but it will no longer be linked to your account.
              <br /><br />
              Once deletion is complete, your account and personal data cannot be restored.
              <br /><br />
              You can request complete deletion of your account and data anytime.
              If you log back in within 24 hours after submitting a deletion request, the request will be automatically canceled.
              <br /><br />
              If you experience any issues, please contact us at{" "}
              <a href="mailto:support@penspace.in">support@penspace.in</a>.
            </p>

            <div className={styles.warningBox}>
              <div className={styles.warningTitle}>⚠️ Important Notice</div>
              <div className={styles.warningText}>
                Accounts that have been inactive for 90 days or more will be automatically deleted for security and data management purposes.
              </div>
            </div>

            {showSuccess && (
              <div className={styles.successMessage}>
                <div className={styles.successTitle}>✅ Account Deletion Initiated</div>
                <div className={styles.successText}>
                  Your account deletion request has been submitted. Your account will be permanently deleted within 24 hours. You can still log in during this period to cancel the deletion.
                </div>
              </div>
            )}

            <form className={styles.deleteForm} onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label htmlFor="email" className={styles.formLabel}>Email Address</label>
                <input
                  type="email"
                  id="email"
                  className={`${styles.formInput} ${emailError ? styles.formInputError : ""}`}
                  placeholder="Enter your email address"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={validateEmail}
                  disabled={isLoading}
                />
                {emailError && (
                  <div className={styles.errorMessage}>{emailError}</div>
                )}
              </div>

              {codeSent && (
                <div className={styles.formGroup}>
                  <label htmlFor="code" className={styles.formLabel}>Code from the email</label>
                  <input
                    type="text"
                    id="code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    className={`${styles.formInput} ${codeError ? styles.formInputError : ""}`}
                    placeholder="Enter the code we emailed you"
                    value={code}
                    onChange={handleCodeChange}
                    disabled={isLoading}
                  />
                  {notice && !codeError && <div className={styles.infoText}>{notice}</div>}
                  {codeError && <div className={styles.errorMessage}>{codeError}</div>}
                  <button type="button" className={styles.backLink} onClick={sendCode} disabled={isLoading}>
                    Send a new code
                  </button>
                </div>
              )}

              <button
                type="submit"
                className={styles.deleteButton}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className={styles.spinner} />
                    <span>Processing...</span>
                  </>
                ) : (
                  <span>{codeSent ? "Delete My Account" : "Email Me a Code"}</span>
                )}
              </button>
            </form>

            <Link to="/" className={styles.backLink}>← Back to Home</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
